import { MessageCircle, X } from "lucide-react";
import { useState } from "react";

interface FloatingWhatsAppProps {
  phoneNumber: string;
  message?: string;
}

export const FloatingWhatsApp = ({ phoneNumber, message = "Hi! I'd like to learn more about your services." }: FloatingWhatsAppProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Build smart WhatsApp URLs based on platform
  const normalized = phoneNumber.replace(/[^\d]/g, "");
  const encodedMessage = encodeURIComponent(message);
  const waMe = `https://wa.me/${normalized}?text=${encodedMessage}`;
  const webWhatsApp = `https://web.whatsapp.com/send?phone=${normalized}&text=${encodedMessage}`;
  const deepLink = `whatsapp://send?phone=${normalized}&text=${encodedMessage}`;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleClick = (e: React.MouseEvent) => {
    console.log('WhatsApp link clicked');
    console.log('Phone number:', normalized);
    console.log('Is mobile:', isMobile);
    
    // For mobile devices, try deep link first
    if (isMobile) {
      e.preventDefault();
      let opened = false;
      
      try {
        const win = window.open(deepLink, '_blank');
        opened = !!win;
      } catch (err) {
        console.log('Deep link failed:', err);
      }
      
      // Fallback to wa.me if deep link doesn't work
      setTimeout(() => {
        if (!opened) {
          window.open(waMe, '_blank');
        }
      }, 600);
    }
    // For desktop, browser will use href (webWhatsApp)
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex items-center gap-3 pointer-events-auto">
      {/* Tooltip */}
      {showTooltip && (
        <div className="glass-panel px-4 py-2 rounded-lg shadow-[0_0_30px_rgba(45,184,255,0.4)] animate-fade-in">
          <p className="text-sm font-medium text-white whitespace-nowrap">
            Need help? Chat with us
          </p>
        </div>
      )}

      {/* WhatsApp Button */}
      <a
        href={isMobile ? waMe : webWhatsApp}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onMouseEnter={() => {
          setIsHovered(true);
          setShowTooltip(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          setShowTooltip(false);
        }}
        className="group relative w-16 h-16 rounded-full bg-gradient-to-r from-neon-glow to-radiant-blue hover:from-radiant-blue hover:to-neon-glow shadow-[0_0_30px_rgba(45,184,255,0.6)] hover:shadow-[0_0_50px_rgba(45,184,255,0.9)] transition-all duration-500 hover:scale-110 flex items-center justify-center pointer-events-auto"
        aria-label="Chat on WhatsApp"
      >
        {/* Icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <MessageCircle 
            className={`w-7 h-7 text-deep-navy transition-transform duration-300 ${
              isHovered ? 'scale-110 rotate-12' : ''
            }`}
          />
        </div>

        {/* Ripple Effect */}
        <div className="absolute inset-0 rounded-full bg-neon-glow opacity-0 group-hover:opacity-20 group-hover:animate-ping" />
      </a>
    </div>
  );
};
